// Generic, business-agnostic ordering + COGS engine. Reads whatever recipes/ingredients/prices a
// business defines. Verified against the "1A. USA INGREDIENT ORDERING" and "1B. USA COGS
// CALCULATION" sheets (see seed extraction).
import type { BusinessData, DemandRow, DemandTrigger, OrderLine, Planning, ProductType, RunItem, RunSizing, RunStatus, Sku, StockLot } from "./types.ts";

// Production lifecycle, in order. INCOMING = runs whose output isn't in sellable stock yet, so it
// counts toward future supply in demand planning.
export const RUN_STATUSES: RunStatus[] = [
  "Planned", "Booked", "Ingredients Ordered", "Ingredients Received", "In Production", "Waiting Pickup", "Delivered", "Cancelled",
];
export const INCOMING_STATUSES: RunStatus[] = [
  "Planned", "Booked", "Ingredients Ordered", "Ingredients Received", "In Production", "Waiting Pickup",
];
export const isIncoming = (s: RunStatus) => INCOMING_STATUSES.includes(s);

export const recipeKey = (flavour: string, strengthValue: number) => `${flavour}|${strengthValue}`;

// ── Unit-of-measure conversion (mass) ────────────────────────────────────────
// Grams per unit for the mass UoMs we accept as receiving inputs. Anything not listed (L, ea, …) is
// a non-mass unit we can't convert without density, so it's assumed to already be in the target unit.
const GRAMS_PER: Record<string, number> = {
  mg: 0.001, g: 1, gram: 1, grams: 1,
  kg: 1000, kilo: 1000, kilogram: 1000, kilograms: 1000,
  oz: 28.349523125, ounce: 28.349523125, ounces: 28.349523125,
  lb: 453.59237, lbs: 453.59237, pound: 453.59237, pounds: 453.59237,
  t: 1_000_000, ton: 1_000_000, tonne: 1_000_000, tonnes: 1_000_000, mt: 1_000_000,
};
/** UoM options offered in the receiving / stock forms (mass units + common non-mass passthroughs). */
export const UOM_OPTIONS = ["kg", "g", "mg", "lb", "oz", "L", "mL", "ea"];
export function uomGrams(unit: string | undefined): number | undefined {
  return GRAMS_PER[(unit || "").trim().toLowerCase()];
}
export function isMassUom(unit: string | undefined): boolean {
  return uomGrams(unit) !== undefined;
}
/**
 * Convert `qty` from `fromUom` into the business's mass unit (`massUnit` = "kg" | "lb"). If `fromUom`
 * is a non-mass unit (L, ea, …) or unrecognised, the quantity is returned unchanged (assumed already
 * in the target unit) — we can't convert volume→mass without a density.
 */
export function convertMass(qty: number, fromUom: string | undefined, massUnit: string | undefined): number {
  const q = Number(qty) || 0;
  const from = uomGrams(fromUom);
  if (from === undefined) return q;
  const to = uomGrams(massUnit) ?? (massUnit === "kg" ? 1000 : 453.59237);
  return q * from / to;
}

// Recipes are entered as grams-per-litre (g:L). Everything else is derived for reference. The stored
// per-unit mass is in the business's pricing mass unit (lb for US, kg for UK).
const G_PER_LB_ = 453.592;
export function gramsPerMassUnit(massUnit?: string): number {
  return (massUnit || "lb").toLowerCase() === "kg" ? 1000 : G_PER_LB_;
}
/** g/L → mass per finished unit (lb or kg), given unit volume (L) and mass unit. */
export function massPerCanFromGL(gPerL: number, unitVolumeL: number, massUnit?: string): number {
  return (Number(gPerL) || 0) * (Number(unitVolumeL) || 0) / gramsPerMassUnit(massUnit);
}
/** Reference conversions shown next to the g:L input. */
export function recipeConversions(gPerL: number, unitVolumeL: number, conv: { gallonL: number; barrelGallon: number; lCan: number; ozG: number }, massUnit?: string) {
  const lPerBbl = conv.gallonL * conv.barrelGallon;
  return {
    ozPerL: (Number(gPerL) || 0) / (conv.ozG || 28.34952),
    lbsPerBbl: (Number(gPerL) || 0) * lPerBbl / G_PER_LB_,
    massPerCan: massPerCanFromGL(gPerL, unitVolumeL, massUnit),
  };
}

export function skuRecipeKey(sku: Sku): string {
  return recipeKey(sku.flavour, sku.strengthValue);
}

/** Packaging components a co-packer confirms on receipt for a run — the product type's build components
 *  flagged `received` (e.g. spirits: Bottles/Labels/Corks/Caps). Falls back to a single container step
 *  named after the type when none are flagged. Order is not significant (confirmed via a checklist). */
export function componentsForRun(data: BusinessData, run: { items: { skuId: string }[] }): string[] {
  const sku = run.items.map((i) => data.skus.find((s) => s.id === i.skuId)).find(Boolean);
  const typeId = sku?.productType || (sku?.purchased ? "gummy" : "can");
  const type = (data.business.productTypes || []).find((t) => t.id === typeId);
  const physical = (type?.build || []).filter((b) => b.received && b.name.trim()).map((b) => b.name.trim());
  if (physical.length) return physical;
  const container = typeId === "bottle" ? "Bottles" : typeId === "gummy" ? "Gummy packs" : "Cans";
  return [container];
}

/** Finished units per US beer barrel (BBL) = litres/barrel ÷ litres/unit. ~330 cans/BBL for a 355ml can. */
export function cansPerBarrel(conv: { gallonL: number; barrelGallon: number; lCan: number }): number {
  const lPerCan = Number(conv.lCan) || 0.355;
  return (Number(conv.gallonL) || 3.78541) * (Number(conv.barrelGallon) || 31) / lPerCan;
}

/** On-hand lbs per ingredient = sum of stock lots. */
export function onHandByIngredient(stock: StockLot[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const lot of stock) {
    out[lot.ingredient] = (out[lot.ingredient] || 0) + (Number(lot.lbs) || 0);
  }
  return out;
}

/** Ingredient price lookup by name. */
function priceMap(data: BusinessData): Record<string, number> {
  const out: Record<string, number> = {};
  for (const i of data.ingredients) out[i.name] = Number(i.pricePerLb) || 0;
  return out;
}
function supplierMap(data: BusinessData): Record<string, string> {
  const out: Record<string, string> = {};
  for (const i of data.ingredients) out[i.name] = i.supplier || "";
  return out;
}
function ingredientMap(data: BusinessData): Map<string, { packSize: number; moq: number }> {
  return new Map(data.ingredients.map((i) => [i.name, { packSize: Number(i.packSize) || 0, moq: Number(i.moq) || 0 }]));
}

/** Round a shortfall up to whole purchase packs, honouring the MOQ. Returns the order qty + #packs. */
export function roundOrderQty(need: number, packSize: number, moq: number): { qty: number; units: number } {
  if (need <= 0) return { qty: 0, units: 0 };
  let qty = Math.max(need, moq || 0);
  if (packSize > 0) {
    const units = Math.ceil(qty / packSize);
    return { qty: units * packSize, units };
  }
  return { qty, units: 0 }; // no pack size configured → order the exact (MOQ-floored) amount
}

export interface RequiredRow {
  ingredient: string;
  supplier: string;
  requiredLbs: number; // includes overage
  onHandLbs: number;
  netNeedLbs: number;  // shortfall before pack/MOQ rounding
  toOrderLbs: number;  // actual order: rounded up to whole packs, at least MOQ
  orderUnits: number;  // number of purchase packs
  excessLbs: number;   // toOrderLbs - netNeedLbs (over-purchase from rounding/MOQ)
  packSize: number;
  moq: number;
  unitCost: number;
  lineCost: number;
  provided: boolean;   // supplied by the canner/co-packer — not ordered, but still in COGS
}

/** Set of supplier names the canner/co-packer provides (we don't order those). */
export function providedSupplierSet(data: BusinessData): Set<string> {
  return new Set((data.suppliers || []).filter((s) => s.providedByCanner).map((s) => s.name));
}

/**
 * Core calculation. Given a set of {skuId, cans}, sums lbs-per-can across every recipe line,
 * applies the business overage, subtracts on-hand, and prices the shortfall.
 */
export function computeRequirements(data: BusinessData, items: RunItem[]): RequiredRow[] {
  const overage = 1 + (Number(data.business.overagePct) || 0);
  const prices = priceMap(data);
  const suppliers = supplierMap(data);
  const provided = providedSupplierSet(data);
  const packInfo = ingredientMap(data);
  const skuById = new Map(data.skus.map((s) => [s.id, s]));

  // ingredient -> required lbs (before overage)
  const required: Record<string, number> = {};
  for (const item of items) {
    const sku = skuById.get(item.skuId);
    if (!sku) continue;
    const cans = Number(item.cans) || 0;
    if (cans <= 0) continue;
    const lines = data.recipes[skuRecipeKey(sku)] || [];
    for (const ln of lines) {
      required[ln.ingredient] = (required[ln.ingredient] || 0) + cans * (Number(ln.lbsPerCan) || 0);
    }
  }

  const onHand = onHandByIngredient(data.stock);
  const rows: RequiredRow[] = [];
  for (const ingredient of Object.keys(required)) {
    const supplier = suppliers[ingredient] || "";
    const isProvided = provided.has(supplier);
    const requiredLbs = required[ingredient] * overage;
    const onHandLbs = onHand[ingredient] || 0;
    const pack = packInfo.get(ingredient) || { packSize: 0, moq: 0 };
    // Canner-provided ingredients aren't ordered by us → nothing to order / no order cost.
    const netNeedLbs = isProvided ? 0 : Math.max(0, requiredLbs - onHandLbs);
    const { qty: toOrderLbs, units: orderUnits } = isProvided ? { qty: 0, units: 0 } : roundOrderQty(netNeedLbs, pack.packSize, pack.moq);
    const unitCost = prices[ingredient] || 0;
    rows.push({
      ingredient,
      supplier,
      requiredLbs,
      onHandLbs,
      netNeedLbs,
      toOrderLbs,
      orderUnits,
      excessLbs: Math.max(0, toOrderLbs - netNeedLbs),
      packSize: pack.packSize,
      moq: pack.moq,
      unitCost,
      lineCost: toOrderLbs * unitCost,
      provided: isProvided,
    });
  }
  // Sort: things to order first (by cost desc), then provided/covered, alphabetically.
  rows.sort((a, b) => (b.toOrderLbs > 0 ? 1 : 0) - (a.toOrderLbs > 0 ? 1 : 0) || b.lineCost - a.lineCost || a.ingredient.localeCompare(b.ingredient));
  return rows;
}

let _seq = 0;
function lineId(): string {
  _seq = (_seq + 1) % 1e6;
  return `ol_${Date.now().toString(36)}_${_seq}`;
}

/** Build order lines from the requirements — excludes canner-provided ingredients (we don't order those). */
export function buildOrderLines(data: BusinessData, items: RunItem[]): OrderLine[] {
  return computeRequirements(data, items).filter((r) => !r.provided).map((r) => ({
    id: lineId(),
    ingredient: r.ingredient,
    supplier: r.supplier,
    requiredLbs: round4(r.requiredLbs),
    onHandLbs: round4(r.onHandLbs),
    netNeedLbs: round4(r.netNeedLbs),
    toOrderLbs: round4(r.toOrderLbs),
    orderUnits: r.orderUnits,
    unitCost: round4(r.unitCost),
    lineCost: round2(r.lineCost),
    received: null,
  }));
}

export function orderTotal(lines: OrderLine[]): number {
  return lines.reduce((a, l) => a + (l.lineCost || 0), 0);
}

// ── COGS ─────────────────────────────────────────────────────────────────────

export interface CogsResult {
  ingredientLines: { ingredient: string; lbsPerCan: number; unitCost: number; costPerCan: number }[];
  ingredientCostPerCan: number;
  productionLines: { name: string; perCan: number }[]; // build components (bottles) or the tolling line (cans)
  productionCostPerCan: number;
  extraLines: { name: string; perCan: number }[]; // freight, COA, etc.
  extrasPerCan: number;
  totalPerCan: number;
  tierIndex: number;
  tierSize: number;
  productType: ProductType;
}

/** The product type for a SKU (default "can"; "gummy" for purchased). Falls back to a bare config —
 * a non-purchased fallback uses the tolling tiers, so a business with no productTypes configured
 * still computes can COGS the old way. */
export function productTypeOf(data: BusinessData, sku: Sku): ProductType {
  const id = sku.productType || (sku.purchased ? "gummy" : "can");
  return (data.business.productTypes || []).find((t) => t.id === id)
    || { id, label: id.charAt(0).toUpperCase() + id.slice(1), usesTiers: !sku.purchased, purchased: !!sku.purchased };
}

/** Cost per unit for a SKU at a given run-size tier index. Make-up depends on the SKU's product type. */
export function computeCogs(data: BusinessData, sku: Sku, tierIndex: number): CogsResult {
  const prices = priceMap(data);
  const type = productTypeOf(data, sku);
  const lines = data.recipes[skuRecipeKey(sku)] || [];
  const ingredientLines = lines
    .map((ln) => {
      const unitCost = prices[ln.ingredient] || 0;
      const costPerCan = (Number(ln.lbsPerCan) || 0) * unitCost;
      return { ingredient: ln.ingredient, lbsPerCan: Number(ln.lbsPerCan) || 0, unitCost, costPerCan };
    })
    .filter((l) => l.costPerCan > 0);
  const ingredientCostPerCan = ingredientLines.reduce((a, l) => a + l.costPerCan, 0);

  const idx = Math.min(Math.max(0, tierIndex), (data.business.cogsTierSizes.length || 1) - 1);
  const tierSize = data.business.cogsTierSizes[idx] || 0;

  // Production make-up, per type:
  //  • cans (usesTiers): the tolling tier is the production TOTAL; `build` components (e.g. the can)
  //    are carved out of it and the remainder shows as "Production (tolling)".
  //  • bottles/gummies: production = the `build` components (fill/bottle/cork/label; gummies+packaging).
  //  • plus any per-SKU adders (e.g. XO lids on 60mg), which are additive on top.
  const buildLines = (type.build || []).map((b) => ({ name: b.name, perCan: Number(b.amount) || 0 }));
  let productionLines: { name: string; perCan: number }[] = [...buildLines];
  if (type.usesTiers) {
    const tier = matchTier(data, sku);
    const toll = tier ? Number(tier.costPerCan[idx]) || 0 : 0;
    const rest = toll - buildLines.reduce((a, l) => a + l.perCan, 0);
    if (rest > 0.0001 || (!buildLines.length && toll > 0)) productionLines.push({ name: "Production (tolling)", perCan: Math.max(0, rest) });
  }
  for (const a of sku.cogsAdders || []) productionLines.push({ name: a.name, perCan: Number(a.amount) || 0 });
  const productionCostPerCan = productionLines.reduce((a, l) => a + l.perCan, 0);

  // Extra adders — only those that apply to this product type. perRun/perSKU amortise over the run
  // size; perPallet/perTruck use this type's units-per-pallet (falling back to the legacy shipping).
  const ship = data.business.shipping || { cansPerPallet: 0, palletsPerTruck: 0 };
  const perPallet = Number(type.unitsPerPallet) || ship.cansPerPallet || 0;
  const perTruck = Number(type.palletsPerTruck) || ship.palletsPerTruck || 0;
  const extraPerCan = (e: { amount: number; basis: string }): number => {
    const amt = Number(e.amount) || 0;
    switch (e.basis) {
      case "perRun":
      case "perSKU": return tierSize > 0 ? amt / tierSize : 0;
      case "perPallet": return perPallet > 0 ? amt / perPallet : 0;
      case "perTruck": {
        if (tierSize <= 0 || perPallet <= 0 || perTruck <= 0) return 0;
        const trucks = Math.ceil((tierSize / perPallet) / perTruck);
        return (amt * trucks) / tierSize;
      }
      default: return amt; // perCan
    }
  };
  const extraLines = (data.business.cogsExtras || [])
    .filter((e) => !e.productType || e.productType === type.id)
    .map((e) => ({ name: e.name, perCan: extraPerCan(e) }));
  const extrasPerCan = extraLines.reduce((a, l) => a + l.perCan, 0);

  return {
    ingredientLines,
    ingredientCostPerCan,
    productionLines,
    productionCostPerCan,
    extraLines,
    extrasPerCan,
    totalPerCan: ingredientCostPerCan + productionCostPerCan + extrasPerCan,
    tierIndex: idx,
    tierSize,
    productType: type,
  };
}

// ── "Always keep N booked" recommendations ───────────────────────────────────
export interface RunRecommendation {
  slot: number;              // 0 = the most immediate missing run
  date: string;              // recommended scheduled date (ISO)
  items: { skuId: string; cans: number }[];
  total: number;             // sum of per-SKU snapped quantities
  rawTotal: number;          // demand-driven total before snapping (for "N cans of demand" display)
  days: number;              // total production days across SKUs (each SKU banded separately)
  basis: "catch-up" | "cycle"; // catch-up = current shortfall; cycle = a normal production cycle
}

/**
 * Snap a desired production quantity up to a valid run size, driven by the canner's daily capacity.
 * A run day yields minRun–maxRun cans; n days cover n×minRun to n×maxRun in `step` increments. So we
 * take the fewest days that can hold the demand (⌈D/maxRun⌉), default each day to the preferred
 * minRun, and only push toward maxRun when the demand needs it — never spinning up a day for scraps.
 *   UK {15000,20000,1000}: 14k→15k · 17k→17k · 23k→30k · 34k→34k · 41k→45k
 * maxRun ≤ minRun (or 0) = no bands: just floor at minRun, round up to step (US {5000,0,1000}).
 */
/** Sensible run-sizing defaults by market, used when a business hasn't set its own (UK bands / US floor). */
export function defaultRunSizing(market?: string): RunSizing {
  const m = (market || "").toUpperCase();
  if (m === "UK") return { minRun: 15000, maxRun: 20000, step: 1000 };
  if (m === "US") return { minRun: 5000, maxRun: 0, step: 1000 };
  return { minRun: 0, maxRun: 0, step: 1000 };
}

export function snapProductionQty(desired: number, s?: Partial<RunSizing>): number {
  const min = Math.max(0, Number(s?.minRun) || 0);
  const step = Number(s?.step) > 0 ? Number(s?.step) : 1;
  const max = Number(s?.maxRun) || 0;
  if (desired <= 0) return 0;
  const stepped = Math.ceil(desired / step) * step;
  if (max > min && min > 0) {
    const days = Math.max(1, Math.ceil(desired / max)); // fewest production days that hold demand
    return Math.max(min * days, stepped);
  }
  return Math.max(min, stepped);
}

/** Production days a single SKU's run takes (banded by daily capacity; 1 when no max set). */
function runDays(cans: number, maxRun: number): number {
  return maxRun > 0 ? Math.max(1, Math.ceil(cans / maxRun)) : (cans > 0 ? 1 : 0);
}

function addDaysISO(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + Math.round(days));
  return d.toISOString().slice(0, 10);
}

/**
 * Recommend the production runs needed to keep `target` bookings in the pipeline. The first missing
 * run is the current shortfall (howMuchToOrder, net of stock + booked runs) scheduled ASAP; further
 * missing runs are a normal cycle (daysToProduce × velocity). The cycle-run DATE is demand-driven:
 * scheduled so production (lead time) completes before cover — current stock + booked production ÷
 * sale rate — hits the reorder point, based on the soonest-to-deplete SKU. Faster sales ⇒ earlier date.
 */
export function recommendMissingRuns(
  rows: DemandRow[], activeBooked: number, planning: Planning, target: number, todayISO: string,
  sizing?: Partial<RunSizing>,
): RunRecommendation[] {
  const missing = Math.max(0, (target || 0) - activeBooked);
  if (missing <= 0) return [];
  const cycle = runwayDaysOf(planning); // days a run covers; also the spacing between cycle runs
  const reorder = (planning.leadTimeDays || 0) + (planning.safetyStockDays || 0);
  const sellable = rows.filter((r) => r.trigger !== "DISCONTINUED" && r.dailyUnits > 0);
  const catchUp = sellable.filter((r) => r.howMuchToOrder > 0).map((r) => ({ skuId: r.skuId, cans: r.howMuchToOrder }));
  const cycleItems = sellable.map((r) => ({ skuId: r.skuId, cans: Math.round(cycle * r.dailyUnits) })).filter((i) => i.cans > 0);

  // Days of cover = (current stock across channels + manufacturer + booked production) ÷ sale rate.
  const coverDays = (r: DemandRow) => (r.dailyUnits > 0 ? (r.inventory + (Number(r.manufacturerStock) || 0) + r.incoming) / r.dailyUnits : Infinity);
  const covered = sellable.filter((r) => r.howMuchToOrder <= 0);
  const basisSkus = covered.length ? covered : sellable;
  const minCover = basisSkus.length ? Math.min(...basisSkus.map(coverDays)) : reorder;
  const firstCycleOffset = Math.max(0, Math.round((isFinite(minCover) ? minCover : reorder) - reorder));

  const maxRun = Number(sizing?.maxRun) || 0;
  const recs: RunRecommendation[] = [];
  let cyclePos = 0;
  for (let k = 0; k < missing; k++) {
    const useCatchUp = k === 0 && catchUp.length > 0;
    const rawItems = useCatchUp ? catchUp : cycleItems;
    if (!rawItems.length) continue;
    const rawTotal = rawItems.reduce((a, i) => a + i.cans, 0);
    // Each SKU is produced separately (own blend), so snap PER SKU to a valid production size.
    const items = rawItems.map((i) => ({ skuId: i.skuId, cans: snapProductionQty(i.cans, sizing) })).filter((i) => i.cans > 0);
    const date = useCatchUp ? todayISO : addDaysISO(todayISO, firstCycleOffset + cyclePos++ * cycle);
    recs.push({
      slot: k,
      date,
      items,
      total: items.reduce((a, i) => a + i.cans, 0),
      rawTotal,
      days: items.reduce((a, i) => a + runDays(i.cans, maxRun), 0),
      basis: useCatchUp ? "catch-up" : "cycle",
    });
  }
  return recs;
}

// ── Shipment tracking (supplier → co-packer) ─────────────────────────────────
export type ArrivalState = "received" | "arrived" | "in_transit" | "pending";
export function lineArrival(line: { received?: unknown; markedArrived?: boolean; trackingNumber?: string; expectedDelivery?: string }, todayISO: string): ArrivalState {
  if ((line.received as { confirmedAt?: number } | null | undefined)?.confirmedAt) return "received";
  const arrived = line.markedArrived || (!!line.expectedDelivery && line.expectedDelivery <= todayISO);
  if (arrived) return "arrived";
  if (line.trackingNumber) return "in_transit";
  return "pending";
}

export function trackingUrl(carrier: string | undefined, num: string | undefined): string | null {
  if (!num) return null;
  const n = encodeURIComponent(num);
  switch ((carrier || "").toLowerCase()) {
    case "ups": return `https://www.ups.com/track?tracknum=${n}`;
    case "fedex": return `https://www.fedex.com/fedextrack/?trknbr=${n}`;
    case "usps": return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${n}`;
    case "dhl": return `https://www.dhl.com/en/express/tracking.html?AWB=${n}`;
    case "royal mail": case "royalmail": return `https://www.royalmail.com/track-your-item#/tracking-results/${n}`;
    default: return `https://www.google.com/search?q=${n}+tracking`;
  }
}

/** Find the production-cost tier row for a SKU: flavour match, strength "ALL" or exact. */
function matchTier(data: BusinessData, sku: Sku) {
  const flav = sku.flavour.toLowerCase();
  const s = sku.strength.toLowerCase();
  const rows = data.cogsTiers.filter((t) => t.flavour.toLowerCase() === flav);
  // exact strength first, then ALL
  return (
    rows.find((t) => t.strength.toLowerCase() === s) ||
    rows.find((t) => t.strength.toLowerCase() === "all") ||
    rows[0] ||
    null
  );
}

// ── Demand planning ──────────────────────────────────────────────────────────
export const DEFAULT_PLANNING: Planning = { leadTimeDays: 60, safetyStockDays: 30, runwayDays: 150, soonExtraDays: 30 };

/** Days of sales a production run should cover. Defaults to 150 when unset (the legacy
 * daysToProduceDays is intentionally NOT used as a fallback — runway replaces it). */
export function runwayDaysOf(p: Planning): number {
  return Number(p.runwayDays) || 150;
}

/**
 * How much to produce and when, for one SKU. Cover is measured INCLUDING booked production
 * (incoming), since that's committed supply — so a SKU with a run already booked isn't flagged
 * urgent just because on-hand is low.
 *   daysRemaining (on hand) = inventory / (monthlyUnits/30)
 *   coverWithIncoming       = (inventory + incoming) / daily
 *   trigger  = NOW  if coverWithIncoming < lead+safety
 *              SOON if coverWithIncoming < lead+safety+soonExtra
 *              else ENOUGH
 *   SCHEDULED = on-hand is below reorder cover but a booked run covers it (nothing more to produce)
 *   howMuchToOrder = runwayDays × daily (a standard production batch covering `runwayDays` of sales),
 *                    shown while cover is below the "soon" line; 0 once there's ample cover.
 */
export function computeDemand(
  skuId: string, name: string, shopifySku: string,
  inventory: number, monthlyUnits: number, p: Planning, incoming = 0,
): DemandRow {
  const dailyUnits = monthlyUnits / 30;
  const reorder = p.leadTimeDays + p.safetyStockDays;
  const soon = reorder + p.soonExtraDays;
  const runway = runwayDaysOf(p);

  let daysRemaining: number | null;
  let trigger: DemandTrigger;
  let howMuchToOrder = 0;
  if (dailyUnits <= 0) {
    daysRemaining = null;
    trigger = inventory > 0 ? "ENOUGH STOCK" : "NO DATA";
  } else {
    daysRemaining = inventory / dailyUnits;
    const coverWithIncoming = (inventory + incoming) / dailyUnits;
    trigger = coverWithIncoming < reorder ? "ORDER NOW" : coverWithIncoming < soon ? "ORDER SOON" : "ENOUGH STOCK";
    howMuchToOrder = coverWithIncoming < soon ? Math.round(runway * dailyUnits) : 0;
    // Low on-hand, but a booked run brings cover back to healthy and nothing more is needed → handled.
    if (incoming > 0 && daysRemaining < reorder && howMuchToOrder === 0) trigger = "SCHEDULED";
  }
  return {
    skuId, name, shopifySku,
    inventory, incoming, monthlyUnits, dailyUnits,
    daysRemaining,
    trigger, howMuchToOrder,
  };
}

// ── Runway projection (for the Booking timeline) ─────────────────────────────
export interface RunwayArrival { day: number; qty: number } // day = offset from today (0)

/**
 * Day-offset from today at which running stock first drops to `floor`, given a constant daily sale
 * rate and dated inbound arrivals (booked productions). Walks arrival-to-arrival: if stock would hit
 * the floor before the next arrival, return that crossing; otherwise consume + receive and continue.
 * Returns Infinity when there are no sales. Can be negative (already past the floor).
 */
export function projectStockoutDay(onHand: number, daily: number, arrivals: RunwayArrival[], floor = 0): number {
  if (daily <= 0) return Infinity;
  const evs = arrivals.filter((a) => a.day >= 0).sort((a, b) => a.day - b.day);
  let stock = onHand;
  let t = 0;
  for (const ev of evs) {
    const daysToFloor = (stock - floor) / daily;
    if (daysToFloor < ev.day - t) return t + daysToFloor;
    stock -= daily * (ev.day - t);
    stock += ev.qty;
    t = ev.day;
  }
  return t + (stock - floor) / daily;
}

/**
 * Day-offset at which stock crosses `floor` on the FINAL declining segment — i.e. after every booked
 * arrival has landed and been consumed. Unlike projectStockoutDay (first crossing), this ignores
 * transient dips that a later booked run refills, so it's the right basis for "when must the NEXT
 * (unbooked) production start". Stock is clamped at 0 across gaps (a stockout doesn't bank negative).
 */
export function projectFinalStockoutDay(onHand: number, daily: number, arrivals: RunwayArrival[], floor = 0): number {
  if (daily <= 0) return Infinity;
  const evs = arrivals.filter((a) => a.day >= 0).sort((a, b) => a.day - b.day);
  let stock = Math.max(0, onHand);
  let t = 0;
  for (const ev of evs) {
    stock = Math.max(0, stock - daily * (ev.day - t));
    stock += ev.qty;
    t = ev.day;
  }
  return t + (stock - floor) / daily;
}

// ── Distribution / channel rebalance ─────────────────────────────────────────
/**
 * "Where to send new production" (planner sheet 2, corrected). Split ONE SKU's freshly-produced
 * cans across channels by each channel's share of *that SKU's* velocity (packs/month). 100% of the
 * production is allocated — nothing is drawn from existing hub stock. If the SKU has no recorded
 * velocity in any channel, everything falls back to the DTC hub (`hubChannelId`, e.g. Shopify), since
 * there's no signal to split on. Rounding remainder is absorbed by the hub so the totals reconcile.
 */
export function splitByChannelVelocity(
  cans: number,
  velocityByChannel: Record<string, number>,
  hubChannelId: string,
): Record<string, number> {
  const out: Record<string, number> = {};
  if (cans <= 0) return out;
  const entries = Object.entries(velocityByChannel);
  const sum = entries.reduce((a, [, v]) => a + Math.max(0, Number(v) || 0), 0);
  if (sum <= 0) { out[hubChannelId] = cans; return out; }
  let allocated = 0;
  for (const [ch, v] of entries) {
    const c = Math.round((cans * Math.max(0, Number(v) || 0)) / sum);
    out[ch] = c;
    allocated += c;
  }
  const rem = cans - allocated; // rounding drift → hub
  if (rem !== 0) out[hubChannelId] = (out[hubChannelId] || 0) + rem;
  return out;
}

export interface RestockRec {
  daysLeft: number | null;
  restock: boolean;
  byHowMuch: number;
}
/**
 * Channel rebalance (planner sheet 3). Restock when cover drops below the LEAD time; size the
 * shipment to (lead + days-to-send) of cover. Works in whatever unit inventory/velocity share
 * (12-packs for UK). byHowMuch rounds up (you ship whole packs).
 */
export function channelRestock(inventory: number, monthlyUnits: number, leadDays: number, sendDays: number): RestockRec {
  const daily = monthlyUnits / 30;
  if (daily <= 0) return { daysLeft: null, restock: false, byHowMuch: 0 };
  const daysLeft = inventory / daily;
  const restock = daysLeft < leadDays;
  const byHowMuch = restock ? Math.max(0, Math.ceil((leadDays + sendDays) * daily - inventory)) : 0;
  return { daysLeft, restock, byHowMuch };
}

/** Pallets needed for a pack count (rounded up). */
export function pallets(packs: number, packsPerPallet: number): number {
  if (packsPerPallet <= 0) return 0;
  return Math.ceil((Number(packs) || 0) / packsPerPallet);
}

export function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}
export function round4(n: number): number {
  return Math.round((n + Number.EPSILON) * 10000) / 10000;
}
